import React from 'react';
import { Card, Row, Col, Statistic, Typography, Space, Avatar, Tag, List } from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  FileTextOutlined, 
  SettingOutlined,
  EyeOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { companyProfileQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import apiService from '../../../services/api';

const { Title, Text } = Typography;

const DashboardTab: React.FC = () => {
  const navigate = useNavigate();

  const { data: profileData } = useQuery({
    queryKey: companyProfileQueryKeys.profile,
    queryFn: async () => {
      const r = await apiService.getCompanyProfile();
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: jobPostingsData, isLoading: jobsLoading } = useQuery({
    queryKey: companyProfileQueryKeys.jobPostings({ page: 1, pageSize: 100 }),
    queryFn: async () => {
      const r = await apiService.getJobPostings({ page: 1, pageSize: 100 });
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: applications = [], isLoading: appsLoading } = useQuery({
    queryKey: companyProfileQueryKeys.applications({}),
    queryFn: async () => {
      const r = await apiService.getCompanyJobApplications();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const jobPostings = jobPostingsData?.data ?? [];
  const loading = jobsLoading || appsLoading;
  const stats = {
    activeJobPostings: jobPostings.filter(job => job.isActive).length,
    totalApplications: applications.length,
    pendingApplications: applications.filter((app: { status: string }) => app.status === 'Pending').length,
    approvedApplications: applications.filter((app: { status: string }) => app.status === 'Approved').length,
  };

  const quickActions = [
    {
      title: 'Şirket Bilgileri',
      description: 'Profil bilgilerinizi güncelleyin',
      icon: <EditOutlined />,
      color: 'var(--primary-500)',
      onClick: () => navigate('/company-panel?tab=profile')
    },
    {
      title: 'İş İlanları',
      description: 'Yeni iş ilanı oluşturun',
      icon: <FileTextOutlined />,
      color: 'var(--success)',
      onClick: () => navigate('/company-panel?tab=job-postings')
    },
    {
      title: 'Başvurular',
      description: 'Gelen başvuruları inceleyin',
      icon: <EyeOutlined />,
      color: '#fa8c16',
      onClick: () => navigate('/company-panel?tab=applications')
    },
    {
      title: 'Ayarlar',
      description: 'Hesap ayarlarınızı yönetin',
      icon: <SettingOutlined />,
      color: '#722ed1',
      onClick: () => navigate('/company-panel?tab=settings')
    }
  ];

  const getRecentActivities = () => {
    const activities: { title: string; description: string; time: string; type: string }[] = [];
    jobPostings.slice(0, 3).forEach(job => {
      activities.push({
        title: 'İş İlanı',
        description: job.title,
        time: new Date(job.createdAt).toLocaleDateString('tr-TR'),
        type: 'job'
      });
    });
    applications.slice(0, 3).forEach((app: { applicantName?: string; jobTitle?: string; appliedAt?: string; createdAt?: string }) => {
      activities.push({
        title: 'Yeni Başvuru',
        description: `${app.applicantName || 'Bilinmeyen'} - ${app.jobTitle || 'İş İlanı'}`,
        time: new Date((app.appliedAt || app.createdAt) || '').toLocaleDateString('tr-TR'),
        type: 'application'
      });
    });
    return activities.slice(0, 5);
  };

  return (
    <div className="panel-tab-content">
      <TabListLoading loading={loading}>
      <Card style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
              Hoş Geldiniz, {profileData?.companyName || 'Şirket'}
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              Şirket panelinize hoş geldiniz. İş ilanlarınızı yönetin, başvuruları inceleyin ve daha fazlasını keşfedin.
            </Text>
          </Col>
          <Col>
            <Avatar 
              size={64} 
              icon={<TeamOutlined />}
              style={{ backgroundColor: 'var(--primary-500)' }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Aktif İş İlanları"
                value={stats.activeJobPostings}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: 'var(--primary-500)' }}
                loading={loading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Toplam Başvuru"
                value={stats.totalApplications}
                prefix={<UserOutlined />}
                valueStyle={{ color: 'var(--success)' }}
                loading={loading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Bekleyen Başvuru"
                value={stats.pendingApplications}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#fa8c16' }}
                loading={loading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Onaylanan Başvuru"
                value={stats.approvedApplications}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#722ed1' }}
                loading={loading}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card title="Hızlı İşlemler" style={{ height: '100%' }}>
            <Row gutter={[16, 16]}>
              {quickActions.map((action, index) => (
                <Col xs={24} sm={12} key={index}>
                  <Card
                    hoverable
                    onClick={action.onClick}
                    style={{ 
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: `2px solid ${action.color}20`,
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ fontSize: '32px', color: action.color, marginBottom: '12px' }}>
                      {action.icon}
                    </div>
                    <Title level={5} style={{ margin: '8px 0', color: '#1f2937' }}>
                      {action.title}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      {action.description}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Son Aktiviteler" style={{ height: '100%' }} loading={loading}>
            {!loading && (
              <List
                dataSource={getRecentActivities()}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          size="small" 
                          icon={item.type === 'job' ? <FileTextOutlined /> : 
                                item.type === 'application' ? <UserOutlined /> : 
                                <EditOutlined />}
                          style={{ 
                            backgroundColor: item.type === 'job' ? 'var(--primary-500)' : 
                                           item.type === 'application' ? 'var(--success)' : 
                                           '#fa8c16' 
                          }}
                        />
                      }
                      title={
                        <Text strong style={{ fontSize: '14px' }}>
                          {item.title}
                        </Text>
                      }
                      description={
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {item.description}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            {item.time}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: '24px' }} loading={loading}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
            <Title level={4} style={{ margin: 0, color: '#1f2937' }}>
              Şirket Durumu
            </Title>
          </Col>
          <Col>
            <Space>
              <Tag color="green" icon={<CheckCircleOutlined />}>
                Aktif
              </Tag>
              <Tag color="blue" icon={<CheckCircleOutlined />}>
                Onaylandı
              </Tag>
            </Space>
          </Col>
        </Row>
        
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                Şirket Adı
              </Text>
              <Text type="secondary">
                {profileData?.companyName || 'Yükleniyor...'}
              </Text>
            </div>
          </Col>
          
        </Row>
      </Card>
      </TabListLoading>
    </div>
  );
};

export default DashboardTab;
